import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  Table,
  TableBody,
  List,
  ListItem,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  TextField,
  InputAdornment,
  Button,
  useTheme,
  ListItemButton,
  Link,
  styled,
  useMediaQuery,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Menu,
  MenuItem,
  Card,
  CardContent,
  ListItemIcon,
  ListItemText,
  CssBaseline,
  Drawer
} from '@mui/material';
import {
  Layers,
  ExpandLess,
  Description,
  MoreVert,
  Search,
  FileCopy,
  Download,
  Share,
  Home,
  Nature,
  Menu as MenuIcon,
  School,
  Close,
  FamilyRestroom,
  Man,
  TrendingUp,
  Public,
  ExpandMore,
  Email,
  WhatsApp,
  Bluetooth,
  Groups,
  LocalHospital,
  Person,
  Grass,
  Spa,
  BugReport,
  Hive,
  Water as WaterIcon,
  Groups3,
  Handshake,
  Diversity3,
  House,
  Roofing,
  Map,
  Pets as LivestockIcon,
  EggAlt as PoultryIcon,
  Cottage as EmergencyHomeIcon, 
  AccessibilityNew, 
  ChildCare, 
  Terrain, 
  Engineering 
} from '@mui/icons-material';
import NavigationBar from '../navbar/NavigationBar';
import Footer from '../navbar/Footer';

// Constantes de style
const drawerWidth = 240;
const primaryColor = '#E30613';
const secondaryColor = '#008D36';
const backgroundColor = '#f5f5f5';

// Composants stylisés
const MainContainer = styled('div')({
  display: 'flex',
  backgroundColor: backgroundColor,
  minHeight: '100vh',
  flexDirection: 'column'
});

const MainContentWithDrawer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexGrow: 1,
  marginTop: '64px',
  [theme.breakpoints.down('sm')]: {
    marginTop: '56px'
  }
}));

const ContentWrapper = styled('main')(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(3),
  backgroundColor: backgroundColor,
  minHeight: 'calc(100vh - 64px)',
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2),
    minHeight: 'calc(100vh - 56px)'
  }
}));

const DrawerWrapper = styled('div')(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  overflow: 'auto',
  height: 'calc(100vh - 64px)',
  borderRight: '1px solid rgba(0, 0, 0, 0.12)',
  backgroundColor: '#fff',
  [theme.breakpoints.down('md')]: {
    position: 'fixed',
    zIndex: 1300
  }
}));

const MobileDrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const StyledTableHead = styled(TableHead)({
  backgroundColor: primaryColor,
  '& th': {
    color: 'white',
    fontWeight: 'bold'
  }
});

const GroupRow = styled(TableRow)({
  backgroundColor: '#e0e0e0',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: '#d0d0d0 !important'
  }
});

const AgeGroupRow = styled(TableRow)({
  '&:hover': {
    backgroundColor: '#f5f5f5 !important'
  }
});

const menuItems = [
  { text: 'Ménages par type', icon: <FamilyRestroom color="primary" />, to: '/RGPHAEDataPage' },
  { text: 'Population par localisation', icon: <Public color="primary" />, to: '/PopulationDataPage' },
  { text: 'Ménages collectifs', icon: <Groups color="primary" />, to: '/PopulationCollectiveHouseholdsPage' },
  { text: 'Sans-abris', icon: <EmergencyHomeIcon color="primary" />, to: '/HomelessPopulationPage' },
  { text: 'Ménages ordinaires', icon: <Home color="primary" />, to: '/PopulationResidentPage' },
  { text: 'Sexe et masculinité', icon: <Man color="primary" />, to: '/PopulationDataPageM' },
  { text: 'Rapport de masculinité', icon: <TrendingUp color="primary" />, to: '/MasculinityRatioPage' },
  { text: 'Population par âge', icon: <ChildCare color="primary" />, to: '/PopulationDataPageAge' },
  { text: "Grands groupes d'âge", icon: <AccessibilityNew color="primary" />, to: '/PopulationAgeGroupPageGrand' },
  { text: "Groupes d'âge (éducation)", icon: <School color="primary" />, to: '/PopulationByAgeGroupPage' },
  { text: 'Groupes d\'âge (santé)', icon: <LocalHospital color="primary" />, to: '/AgeGroupPopulationPage' },
  { text: 'Adultes (18+)', icon: <Person color="primary" />, to: '/AdultPopulationPage' },
  
  // Agriculture
  { text: 'Ménages agricoles', icon: <Nature color="primary" />, to: '/AgriculturalHouseholdsPage' },
  { text: 'Pratiques agricoles', icon: <Grass color="primary" />, to: '/AgriculturalHouseholdsPages' },
  { text: 'Types de cultures', icon: <Spa color="primary" />, to: '/AgriculturalHouseholdsPage20' },
  
  // Élevage
  { text: 'Élevage par espèces', icon: <LivestockIcon color="primary" />, to: '/FarmingHouseholdsPage' },
  { text: 'Élevage de volailles', icon: <PoultryIcon color="primary" />, to: '/poultryFarmingPage' },
  { text: 'Élevage spécial', icon: <BugReport color="primary" />, to: '/insectFarmingHouseholdsPage' },
  { text: 'Répartition cheptel', icon: <LivestockIcon color="primary" />, to: '/livestockDistributionPage' },
  { text: 'Répartition volailles', icon: <PoultryIcon color="primary" />, to: '/poultryDistributionPage' },
  
  // Matériel agricole
  { text: 'Équipements agricoles', icon: <Engineering color="primary" />, to: '/agriculturalEquipmentPage' },
  { text: 'Apiculture', icon: <Hive color="primary" />, to: '/beekeepingHouseholdsPage' },
  { text: 'Pêche/aquaculture', icon: <WaterIcon color="primary" />, to: '/fishingHouseholdsPage' },
  
  // Associations
  { text: 'Interventions associations', icon: <Groups3 color="primary" />, to: '/associationInterventionPage' },
  { text: 'Interventions coopératives', icon: <Handshake color="primary" />, to: '/cooperativeInterventionPage' },
  { text: 'Interventions ONG', icon: <Diversity3 color="primary" />, to: '/interventionProjectsPage' },
  
  // Habitat
  { text: "Type d'habitat", icon: <House color="primary" />, to: '/housingDistributionPage' },
  { text: 'Matériau toit', icon: <Roofing color="primary" />, to: '/roofMaterialPage' },
  { text: 'Matériau sol', icon: <Layers color="primary" />, to: '/housingMaterialPage' },
  { text: 'Ménages agricoles', icon: <Terrain color="primary" />, to: '/agriculturalHouseholdsPageProvCom' },
  { text: 'Population par âge/résidence', icon: <Map color="primary" />, to: '/populationAgeResidencePage' },
];

// Données améliorées avec titres clairs
const AGE_DATA = {
  title: "Répartition de la population des ménages ordinaires par groupes d'âges selon le sexe",
  description: "Données issues du dernier recensement national montrant la distribution démographique par tranche d'âge et par sexe",
  source: "Institut National de la Statistique du Burundi - Année 2023",
  total: {
    title: "Population totale des ménages ordinaires",
    total: "12 220 934",
    masculin: "5 823 075",
    feminin: "6 397 859",
    pctFeminin: "52,4",
    pctMasculin: "47,6"
  },
  groups: [
    { 
      title: "Population préscolaire",
      label: "0 - 4 ans", 
      total: "2 041 065", 
      masculin: "1 022 870", 
      feminin: "1 018 195", 
      pctM: "50,2", 
      pctF: "49,8" 
    },
    { 
      title: "Population scolaire primaire",
      label: "5 - 9 ans", 
      total: "1 816 286", 
      masculin: "912 186", 
      feminin: "904 100", 
      pctM: "50,2", 
      pctF: "49,8" 
    },
    { 
      title: "Population scolaire secondaire",
      label: "10 - 14 ans", 
      total: "1 648 455", 
      masculin: "819 866", 
      feminin: "828 589", 
      pctM: "49,7", 
      pctF: "50,3" 
    },
    { 
      title: "Adolescents",
      label: "15 - 19 ans", 
      total: "1 336 094", 
      masculin: "628 707", 
      feminin: "707 387", 
      pctM: "47,0", 
      pctF: "53,0" 
    },
    { 
      title: "Jeunes adultes",
      label: "20 - 24 ans", 
      total: "1 003 805", 
      masculin: "438 251", 
      feminin: "565 554", 
      pctM: "43,6", 
      pctF: "56,4" 
    },
    { 
      title: "Jeunes actifs",
      label: "25 - 29 ans", 
      total: "778 950", 
      masculin: "340 973", 
      feminin: "437 977", 
      pctM: "43,8", 
      pctF: "56,2" 
    },
    { 
      title: "Adultes jeunes",
      label: "30 - 34 ans", 
      total: "759 217", 
      masculin: "344 621", 
      feminin: "414 596", 
      pctM: "45,4", 
      pctF: "54,6" 
    },
    { 
      title: "Adultes actifs",
      label: "35 - 39 ans", 
      total: "661 689", 
      masculin: "304 499", 
      feminin: "357 190", 
      pctM: "46,0", 
      pctF: "54,0" 
    },
    { 
      title: "Adultes matures",
      label: "40 - 44 ans", 
      total: "541 911", 
      masculin: "254 365", 
      feminin: "287 546", 
      pctM: "46,9", 
      pctF: "53,1" 
    },
    { 
      title: "Adultes pré-séniors",
      label: "45 - 49 ans", 
      total: "366 748", 
      masculin: "175 026", 
      feminin: "191 722", 
      pctM: "47,7", 
      pctF: "52,3" 
    },
    { 
      title: "Séniors débutants",
      label: "50 - 54 ans", 
      total: "334 058", 
      masculin: "153 938", 
      feminin: "180 120", 
      pctM: "46,1", 
      pctF: "53,9" 
    },
    { 
      title: "Séniors actifs",
      label: "55 - 59 ans", 
      total: "245 143", 
      masculin: "115 823", 
      feminin: "129 320", 
      pctM: "47,3", 
      pctF: "52,7" 
    },
    { 
      title: "Pré-retraités",
      label: "60 - 64 ans", 
      total: "259 545", 
      masculin: "120 718", 
      feminin: "138 827", 
      pctM: "46,5", 
      pctF: "53,5" 
    },
    { 
      title: "Jeunes retraités",
      label: "65 - 69 ans", 
      total: "173 249", 
      masculin: "82 697", 
      feminin: "90 552", 
      pctM: "47,8", 
      pctF: "52,2" 
    },
    { 
      title: "Retraités",
      label: "70 - 74 ans", 
      total: "129 754", 
      masculin: "58 165", 
      feminin: "71 589", 
      pctM: "44,8", 
      pctF: "55,2" 
    },
    { 
      title: "Personnes âgées",
      label: "75 - 79 ans", 
      total: "58 267", 
      masculin: "25 362", 
      feminin: "32 905", 
      pctM: "43,5", 
      pctF: "56,5" 
    },
    { 
      title: "Grands âges",
      label: "80 ans ou plus", 
      total: "66 698", 
      masculin: "25 008", 
      feminin: "41 690", 
      pctM: "37,5", 
      pctF: "62,5" 
    }
  ]
};

const PopulationAgeGroupPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileOpen, setMobileOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [shareAnchorEl, setShareAnchorEl] = useState(null);
  const [downloadAnchorEl, setDownloadAnchorEl] = useState(null);
  const [openKeyStats, setOpenKeyStats] = useState(true);

  const handleDrawerToggle = useCallback(() => {
    setMobileOpen(prev => !prev);
  }, []);

  const toggleGroup = useCallback((groupLabel) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupLabel]: !prev[groupLabel]
    }));
  }, []);

  const handleShareMenuOpen = useCallback((event) => {
    setShareAnchorEl(event.currentTarget);
  }, []);

  const handleDownloadMenuOpen = useCallback((event) => {
    setDownloadAnchorEl(event.currentTarget);
  }, []);

  const handleMenuClose = useCallback(() => {
    setShareAnchorEl(null);
    setDownloadAnchorEl(null);
  }, []);
  const filteredGroups = useMemo(() => 
    AGE_DATA.groups.filter(group =>
      group.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      group.title.toLowerCase().includes(searchTerm.toLowerCase())
  ), [searchTerm]);

  const keyStats = useMemo(() => ({
    totalPopulation: AGE_DATA.total.total,
    proportionFemmes: AGE_DATA.total.pctFeminin,
    proportionHommes: AGE_DATA.total.pctMasculin,
    oldestGroup: AGE_DATA.groups[AGE_DATA.groups.length - 1],
    youngestGroup: AGE_DATA.groups[0],
    largestGroup: AGE_DATA.groups.reduce((prev, curr) => 
      parseFloat(curr.total.replace(/\s/g, '')) > parseFloat(prev.total.replace(/\s/g, '')) ? curr : prev
    ),
    smallestGroup: AGE_DATA.groups.reduce((prev, curr) => 
      parseFloat(curr.total.replace(/\s/g, '')) < parseFloat(prev.total.replace(/\s/g, '')) ? curr : prev
    )
  }), []);

  // Composant du menu latéral
  const drawer = (
    <div>
      {isMobile && (
        <MobileDrawerHeader>
          <IconButton onClick={handleDrawerToggle}>
            <Close color="primary" />
          </IconButton>
        </MobileDrawerHeader>
      )}
      <Divider />
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              component={Link}
              to={item.to}
              onClick={isMobile ? handleDrawerToggle : undefined}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </div>
  );

  return (
    <MainContainer>
      <CssBaseline />
      <NavigationBar />
      <MainContentWithDrawer>
        {!isMobile ? (
          <DrawerWrapper>
            {drawer}
          </DrawerWrapper>
        ) : (
          <Drawer
            variant="temporary"
            open={mobileOpen}
            onClose={handleDrawerToggle}
            ModalProps={{
              keepMounted: true,
            }}
            sx={{
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
            }}
          >
            {drawer}
          </Drawer>
        )}

        <ContentWrapper>
          <Container maxWidth="lg">
            {isMobile && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                <Button 
                  variant="outlined" 
                  startIcon={<MenuIcon />} 
                  onClick={handleDrawerToggle}
                  sx={{ color: primaryColor, borderColor: primaryColor }}
                >
                  Menu
                </Button>
              </Box>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Paper elevation={2} sx={{ 
                  p: { xs: 2, md: 3 }, 
                  mb: 3, 
                  borderLeft: `4px solid ${primaryColor}`,
                  borderRadius: 2
                }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', sm: 'row' }, 
                    justifyContent: 'space-between', 
                    alignItems: { xs: 'flex-start', sm: 'center' }, 
                    mb: 3,
                    gap: 2
                  }}>
                    <Box>
                      <Typography variant="h6" component="h1" fontWeight="bold" color="primary">
                        {AGE_DATA.title}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Grid container spacing={1} style={{ marginTop: '5px', marginBottom: '5px' }}>
                    <Grid item xs={12} sm={6} md={6}></Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        size="small"
                        placeholder="Rechercher groupe d'âge ou catégorie..."
                        variant="outlined"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Search />
                            </InputAdornment>
                          ),
                          sx: {
                            borderRadius: 1
                          }
                        }}
                        sx={{ 
                          width: { xs: '100%', sm: 480 },
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'divider'
                            },
                            '&:hover fieldset': {
                              borderColor: 'primary.main'
                            }
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                  
                  <Accordion 
                    expanded={openKeyStats} 
                    onChange={() => setOpenKeyStats(!openKeyStats)} 
                    sx={{ 
                      mb: 3,
                      '&:before': {
                        display: 'none'
                      }
                    }}
                  >
                    <AccordionSummary 
                      expandIcon={<ExpandMore />}
                      sx={{
                        backgroundColor: 'background.paper',
                        borderRadius: 1,
                        '&.Mui-expanded': {
                          minHeight: 48,
                          margin: 0
                        }
                      }}
                    >
                      <Typography variant="subtitle1" fontWeight="bold">
                        Indicateurs Clés par Groupe d'Âge
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 2 }}>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Population Totale
                              </Typography>
                              <Typography variant="h5" fontWeight="bold" color="primary">
                                {keyStats.totalPopulation}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Ménages ordinaires
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Répartition par sexe
                              </Typography>
                              <Box display="flex" justifyContent="space-between">
                                <Box>
                                  <Typography variant="body2">Hommes</Typography>
                                  <Typography variant="h6" fontWeight="bold" color="primary">
                                    {keyStats.proportionHommes}%
                                  </Typography>
                                </Box>
                                <Box>
                                  <Typography variant="body2">Femmes</Typography>
                                  <Typography variant="h6" fontWeight="bold" color={secondaryColor}>
                                    {keyStats.proportionFemmes}%
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${primaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Groupe le plus nombreux
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.largestGroup.title}
                              </Typography>
                              <Typography variant="body2">
                                {keyStats.largestGroup.label} - {keyStats.largestGroup.total}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ({keyStats.largestGroup.pctM}% hommes, {keyStats.largestGroup.pctF}% femmes)
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6} lg={3}>
                          <Card sx={{ height: '100%', borderLeft: `4px solid ${secondaryColor}` }}>
                            <CardContent>
                              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                Groupe le moins nombreux
                              </Typography>
                              <Typography variant="h6" fontWeight="bold">
                                {keyStats.smallestGroup.title}
                              </Typography>
                              <Typography variant="body2">
                                {keyStats.smallestGroup.label} - {keyStats.smallestGroup.total}
                              </Typography>
                              <Typography variant="body2" color="text.secondary">
                                ({keyStats.smallestGroup.pctM}% hommes, {keyStats.smallestGroup.pctF}% femmes)
                              </Typography>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    </AccordionDetails>
                  </Accordion>

                  <TableContainer 
                    component={Paper} 
                    sx={{ 
                      mb: 3, 
                      overflowX: 'auto',
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 2
                    }}
                  >
                    <Table size="small" aria-label="Répartition par groupes d'âge" >
                      <StyledTableHead>
                        <TableRow>
                          <TableCell>Catégorie / Groupe d'âges</TableCell>
                          <TableCell align="right">Population totale</TableCell>
                          <TableCell align="right">Hommes (nombre et %)</TableCell>
                          <TableCell align="right">Femmes (nombre et %)</TableCell>
                        </TableRow>
                      </StyledTableHead>
                      <TableBody>
                        <GroupRow>
                          <TableCell sx={{ fontWeight: 'bold' }}>{AGE_DATA.total.title}</TableCell>
                          <TableCell align="right">{AGE_DATA.total.total}</TableCell>
                          <TableCell align="right">
                            {AGE_DATA.total.masculin} ({AGE_DATA.total.pctMasculin}%)
                          </TableCell>
                          <TableCell align="right">
                            {AGE_DATA.total.feminin} ({AGE_DATA.total.pctFeminin}%)
                          </TableCell>
                        </GroupRow>
                        {filteredGroups.map((group, index) => (
                          <React.Fragment key={index}>
                            <AgeGroupRow
                              hover
                              onClick={() => toggleGroup(group.label)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                  {expandedGroups[group.label] ? <ExpandLess /> : <ExpandMore />}
                                  <Box>
                                    <Typography variant="body2" fontWeight="bold">
                                      {group.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {group.label}
                                    </Typography>
                                  </Box>
                                </Box>
                              </TableCell>
                              <TableCell align="right">{group.total}</TableCell>
                              <TableCell align="right">{group.masculin} ({group.pctM}%)</TableCell>
                              <TableCell align="right">{group.feminin} ({group.pctF}%)</TableCell>
                            </AgeGroupRow>
                            {expandedGroups[group.label] && (
                            <TableRow>
  <TableCell colSpan={4} sx={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}>
    <Typography variant="caption" color="text.secondary">
      <strong>Caractéristiques :</strong> Cette tranche d'âge représente {group.total} personnes, 
      soit {(parseFloat(group.total.replace(/\s/g, '')) / parseFloat(AGE_DATA.total.total.replace(/\s/g, '')) * 100).toFixed(2)}% 
      de la population totale. La répartition par sexe montre {group.pctM}% d'hommes et {group.pctF}% de femmes.
    </Typography>
  </TableCell>
</TableRow>

                            )}
                          </React.Fragment>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
   {/*
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1
                  }}>
                  
                       
                    <Box sx={{ 
                      display: 'flex', 
                      gap: 1,
                      flexWrap: 'wrap'
                    }}>
                      <Tooltip title="Copier le tableau">
                        <Button
                          variant="outlined"
                          onClick={handleCopyTable}
                          startIcon={<FileCopy />}
                          sx={{ 
                            color: 'text.primary',
                            borderColor: 'divider',
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: 'rgba(227, 6, 19, 0.04)'
                            }
                          }}
                        >
                          Copier
                        </Button>
                      </Tooltip>

                      <Tooltip title="Télécharger les données">
                        <Button
                          variant="outlined"
                          onClick={handleDownloadMenuOpen}
                          startIcon={<Download />}
                          sx={{ 
                            color: 'text.primary',
                            borderColor: 'divider',
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: 'rgba(227, 6, 19, 0.04)'
                            }
                          }}
                        >
                          Télécharger
                        </Button>
                      </Tooltip>
                      <Menu
                        anchorEl={downloadAnchorEl}
                        open={Boolean(downloadAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <MenuItem onClick={() => downloadFile('xlsx')} dense>
                          <ListItemIcon>
                            <Description fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Excel (.xlsx)</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => downloadFile('csv')} dense>
                          <ListItemIcon>
                            <Description fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>CSV (.csv)</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => downloadFile('pdf')} dense>
                          <ListItemIcon>
                            <Description fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>PDF (.pdf)</ListItemText>
                        </MenuItem>
                      </Menu>

                      <Tooltip title="Partager">
                        <Button
                          variant="outlined"
                          onClick={handleShareMenuOpen}
                          startIcon={<Share />}
                          sx={{ 
                            color: 'text.primary',
                            borderColor: 'divider',
                            '&:hover': {
                              borderColor: 'primary.main',
                              backgroundColor: 'rgba(227, 6, 19, 0.04)'
                            }
                          }}
                        >
                          Partager
                        </Button>
                      </Tooltip>
                      <Menu
                        anchorEl={shareAnchorEl}
                        open={Boolean(shareAnchorEl)}
                        onClose={handleMenuClose}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right',
                        }}
                        transformOrigin={{
                          vertical: 'top',
                          horizontal: 'right',
                        }}
                      >
                        <MenuItem onClick={() => shareVia('whatsapp')} dense>
                          <ListItemIcon>
                            <WhatsApp fontSize="small" color="success" />
                          </ListItemIcon>
                          <ListItemText>WhatsApp</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => shareVia('email')} dense>
                          <ListItemIcon>
                            <Email fontSize="small" color="action" />
                          </ListItemIcon>
                          <ListItemText>Email</ListItemText>
                        </MenuItem>
                        <MenuItem onClick={() => shareVia('bluetooth')} dense>
                          <ListItemIcon>
                            <Bluetooth fontSize="small" color="primary" />
                          </ListItemIcon>
                          <ListItemText>Bluetooth</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={() => shareVia('other')} dense>
                          <ListItemIcon>
                            <MoreVert fontSize="small" />
                          </ListItemIcon>
                          <ListItemText>Autre méthode</ListItemText>
                        </MenuItem>
                      </Menu>
                    </Box>
                  </Box>
                  */}
                </Paper>

                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 2, 
                    backgroundColor: 'background.default', 
                    borderLeft: `4px solid ${secondaryColor}`,
                    borderRadius: 2
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom color="primary" fontWeight="bold">
                    Notes méthodologiques
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Les données présentées sont issues du dernier recensement général de la population et de l'habitat. 
                    La population des ménages ordinaires exclut les ménages collectifs (internats, casernes, etc.) et les sans-abris.
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Les pourcentages sont calculés par rapport à la population totale de chaque tranche d'âge. 
                    Les écarts entre hommes et femmes deviennent significatifs à partir de l'adolescence.
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Les données peuvent comporter une marge d'erreur de ±1% due aux arrondis statistiques.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </ContentWrapper>
      </MainContentWithDrawer>
      <Footer />
    </MainContainer>
  );
};

export default PopulationAgeGroupPage;